import React, { useRef, useState } from "react";
import { formatOldUrl } from "../../utils/cloudApi";

export default function AutoImage({
  field,
  updateField,
  selectField,
  startDrag,
  stopDrag,
  isSelected,
  zoom,
  onDoubleClick,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const { id, src, baseLeft, baseTop, baseWidth, baseHeight } = field;

  // Logical dimensions (un‑zoomed)
  const logicalWidth = field.width ?? baseWidth;
  const logicalHeight = field.height ?? baseHeight;

  // Screen dimensions & position
  const leftPx = baseLeft * zoom;
  const topPx = baseTop * zoom;
  const widthPx = logicalWidth * zoom;
  const heightPx = logicalHeight * zoom;

  // Refs to track the pointer start and size at drag start
  const startPosRef = useRef({ x: 0, y: 0 });
  const startSizeRef = useRef({ w: logicalWidth, h: logicalHeight });
  const startLeftRef = useRef(baseLeft);
  const startTopRef = useRef(baseTop);
  const handleSideRef = useRef("se");

  // Drag handlers
  const onImageMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectField(id);
    startDrag(e, id);
  };
  const onImageMouseUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    stopDrag(e, id);
  };

  // Begin resize: record side + start metrics + capture pointer
  const onResizePointerDown = (side) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectField(id);

    handleSideRef.current = side;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    startSizeRef.current = { w: logicalWidth, h: logicalHeight };
    startLeftRef.current = baseLeft;
    startTopRef.current = baseTop;

    e.currentTarget.setPointerCapture(e.pointerId);
  };

  // As pointer moves: compute delta based on corner
  // inside AutoImage…
  const onResizePointerMove = (e) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;

    // 1) raw deltas in logical (un-zoomed) pixels
    const dx = (e.clientX - startPosRef.current.x) / zoom;
    const dy = (e.clientY - startPosRef.current.y) / zoom;

    // 2) determine corner direction
    const side = handleSideRef.current; // "nw","ne","sw","se"
    const signX = side === "ne" || side === "se" ? +1 : -1;
    const signY = side === "sw" || side === "se" ? +1 : -1;

    // apply direction to deltas
    const ddx = dx * signX;
    const ddy = dy * signY;

    // original logical size
    const w0 = startSizeRef.current.w;
    const h0 = startSizeRef.current.h;
    const ratio = h0 / w0;

    let newW, newH;

    // 3) pick the dominant axis for scaling
    if (Math.abs(ddx) > Math.abs(ddy)) {
      // drive width
      newW = Math.max(20, w0 + ddx);
      newH = newW * ratio;
    } else {
      // drive height
      newH = Math.max(20, h0 + ddy);
      newW = newH / ratio;
    }

    // 4) reposition top/left for NW & SW & NE
    let newLeft = startLeftRef.current;
    let newTop = startTopRef.current;
    if (signX === -1) {
      // west handles moved the left edge
      newLeft = startLeftRef.current + (w0 - newW);
    }
    if (signY === -1) {
      // north handles moved the top edge
      newTop = startTopRef.current + (h0 - newH);
    }

    // 5) write back
    updateField(id, {
      baseWidth: newW,
      baseHeight: newH,
      baseLeft: newLeft,
      baseTop: newTop,
    });
  };

  // Release capture
  const onResizePointerUp = (e) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={onDoubleClick}
      style={{
        position: "absolute",
        left: leftPx,
        top: topPx,
        width: widthPx,
        height: heightPx,
      }}
    >
      {/* 1) The image, draggable */}
      <img
        src={formatOldUrl(src)}
        alt=""
        onMouseDown={onImageMouseDown}
        onMouseUp={onImageMouseUp}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          cursor: "move",
          outline:
            isSelected || isHovered
              ? "1px dashed #60B5FF"
              : "1px solid transparent",
          userSelect: "none",
        }}
      />

      {/* 2) Four resize handles */}
      {isSelected && (
        <>
          {/* NW */}
          <div
            onPointerDown={onResizePointerDown("nw")}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            style={handleStyle("nw")}
          />
          {/* NE */}
          <div
            onPointerDown={onResizePointerDown("ne")}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            style={handleStyle("ne")}
          />
          {/* SW */}
          <div
            onPointerDown={onResizePointerDown("sw")}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            style={handleStyle("sw")}
          />
          {/* SE */}
          <div
            onPointerDown={onResizePointerDown("se")}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            style={handleStyle("se")}
          />
        </>
      )}
    </div>
  );
}

// helper to position each handle
function handleStyle(corner) {
  const base = {
    position: "absolute",
    width: 12,
    height: 12,
    background: "#3b82f6",
    border: "1px solid #60B5FF",
    zIndex: 30,
    boxSizing: "border-box",
  };
  switch (corner) {
    case "nw":
      return {
        ...base,
        top: 0,
        left: 0,
        cursor: "nw-resize",
        transform: "translate(-50%,-50%)",
      };
    case "ne":
      return {
        ...base,
        top: 0,
        right: 0,
        cursor: "ne-resize",
        transform: "translate(50%,-50%)",
      };
    case "sw":
      return {
        ...base,
        bottom: 0,
        left: 0,
        cursor: "sw-resize",
        transform: "translate(-50%,50%)",
      };
    default:
      return {
        ...base,
        bottom: 0,
        right: 0,
        cursor: "se-resize",
        transform: "translate(50%,50%)",
      };
  }
}
